import React from 'react';
import { Check, Play } from 'react-feather';

import { isVideoFilename } from '@/hooks/client/useContentHubSearch';
import { getContentHubVideoPreviewUrl, isVideoUrl } from '@/utils/mediaUtils';

import styles from './ContentHubMediaCard.module.css';

type Props = {
  media: Api.Client.ContentHubMedia;
  selectionMode: boolean;
  isSelected: boolean;
  onToggleSelected: () => void;
};

export function ContentHubMediaCard({ media, selectionMode, isSelected, onToggleSelected }: Props) {
  const isVideo = isVideoFilename(media.filename) || isVideoUrl(media.contenthub_url);
  const [videoPreviewError, setVideoPreviewError] = React.useState(false);
  const videoPreviewUrl = isVideo ? getContentHubVideoPreviewUrl(media.contenthub_url) : undefined;

  const cardProps = selectionMode
    ? {
        role: 'button',
        tabIndex: 0,
        onClick: onToggleSelected,
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') onToggleSelected();
        }
      }
    : {
        href: media.contenthub_url,
        target: '_blank',
        rel: 'noreferrer'
      };

  const WrapperTag = (selectionMode ? 'div' : 'a') as 'div' | 'a';

  return (
    <WrapperTag className={styles.card} title={media.filename} {...cardProps}>
      <div className={styles.thumb}>
        {selectionMode && (
          <div className={`${styles.checkbox} ${isSelected ? styles.checkboxSelected : ''}`}>
            {isSelected && <Check size={16} />}
          </div>
        )}
        {isVideo ? (
          <>
            {videoPreviewUrl && !videoPreviewError ? (
              <img
                className={styles.img}
                src={videoPreviewUrl}
                alt={media.filename}
                loading="lazy"
                onError={() => setVideoPreviewError(true)}
              />
            ) : (
              <div className={styles.videoFallback} aria-hidden>
                <Play size={22} />
              </div>
            )}
            <div className={styles.playOverlay} aria-hidden>
              <Play size={18} />
            </div>
          </>
        ) : (
          <img
            className={styles.img}
            src={media.contenthub_url}
            alt={media.filename}
            loading="lazy"
          />
        )}
      </div>

      <div className={styles.meta}>
        <div className={styles.filename}>{media.filename}</div>
      </div>
    </WrapperTag>
  );
}
